import type { RecipeStep, CookSessionApiItem } from "@recipevault/shared";

export interface CookProgress {
  currentIndex: number; // 1-based position among non-prep steps
  total: number; // count of non-prep steps
  percentDone: number; // 0–100
  remainingMin: number; // sum of estimatedMin over not-done non-prep steps
}

/**
 * Cooking progress over the NON-prep steps (prep/mise-en-place is cleared before
 * the live cook, so it isn't part of the "Step N of M" denominator). `currentIndex`
 * is the 1-based position of `currentStepId` among non-prep steps, falling back to
 * doneCount+1 when there's no current step. remainingMin treats a null estimate as 0.
 */
export function progressOf(
  steps: ReadonlyArray<RecipeStep>,
  stepStates: CookSessionApiItem["stepStates"],
  currentStepId: string | null,
): CookProgress {
  const cookSteps = steps.filter((s) => s.kind !== "prep");
  const total = cookSteps.length;
  const isDone = (id: string): boolean => stepStates[id]?.status === "done";
  const doneCount = cookSteps.filter((s) => isDone(s.id)).length;

  const idx = currentStepId ? cookSteps.findIndex((s) => s.id === currentStepId) : -1;
  const currentIndex = idx >= 0 ? idx + 1 : Math.min(doneCount + 1, Math.max(total, 1));

  const percentDone = total ? Math.round((doneCount / total) * 100) : 0;
  const remainingMin = cookSteps
    .filter((s) => !isDone(s.id))
    .reduce((sum, s) => sum + (s.estimatedMin ?? 0), 0);

  return { currentIndex, total, percentDone, remainingMin };
}
